/** The catalog records' Edit gate (plan §2 "States"): the header's Edit from the manage
 *  permission, the environment's switch (`CONSOLE_CATALOG_EDIT_ENABLED`) and any other reason
 *  Edit cannot open yet, in `RecordHeaderActions`' terms. */
import { EDITING_OFF } from '@/features/records/header/editingOff';
import type { RecordHeaderActionsProps } from '@/features/records/header/RecordHeaderActions';

/** Why Edit is blocked while the switch is off: the blocked Edit's tab stop and tooltip, and
 *  the Actions menu's note — the records' one wording of the switch. */
export const CATALOG_EDITING_OFF = EDITING_OFF;

/** Why Edit is shown disabled on a record page whose EDIT half has not landed yet. */
export const CATALOG_EDIT_NOT_HERE_YET = 'Editing isn’t on this page yet';

/** Why Edit is blocked on a record without an operator (an orphan activity): its banner says
 *  so in the same words — nothing can edit it, the dashboard included. */
export const CATALOG_NO_OPERATOR = 'No owning operator';

/** Why Edit is blocked while the edit catalog or the record's edit context could not load
 *  (the page says so in a banner with Retry). */
export const CATALOG_EDITOR_LOAD_FAILED = 'The editor couldn’t load';

/** The not-here-yet reason, pointing to the dashboard when the record has a page there. Two
 *  sentences, no dash: the blocked Edit's name already reads "Edit — <reason>". */
export function catalogEditNotHereYet(dashboardUrl: string | null | undefined): string {
  return dashboardUrl ? `${CATALOG_EDIT_NOT_HERE_YET}. Use Actions › Open in dashboard.` : CATALOG_EDIT_NOT_HERE_YET;
}

/** With the permission and the switch on, Edit; with the permission and the switch off, Edit
 *  disabled with the reason; without the permission, no Edit at all. The Actions menu says
 *  why whenever the switch is off.
 *
 *  `blockedReason`: with the permission and the switch on, another reason Edit cannot open
 *  yet — Edit shown disabled with these words, in its tab stop, tooltip and the Actions
 *  menu's note. The switch's words win while it is off. */
export function catalogEditGate(
  canEdit: boolean,
  editingDisabled: boolean,
  blockedReason: string | null = null,
): Pick<RecordHeaderActionsProps, 'canEdit' | 'editBlocked' | 'note'> {
  if (canEdit && blockedReason && !editingDisabled) {
    return { canEdit: false, editBlocked: blockedReason, note: blockedReason };
  }
  return {
    canEdit: canEdit && !editingDisabled,
    editBlocked: canEdit && editingDisabled ? CATALOG_EDITING_OFF : null,
    note: editingDisabled ? CATALOG_EDITING_OFF : null,
  };
}
